import {CommonModule, DOCUMENT, isPlatformBrowser} from '@angular/common';
import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, NgZone,
  AfterViewChecked, OnChanges, OnDestroy, Output, PLATFORM_ID, ViewChild,
} from '@angular/core';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonModule} from 'primeng/button';
import {Dialog, DialogModule} from 'primeng/dialog';
import {SkeletonModule} from 'primeng/skeleton';
import Swiper, {SwiperOptions, Zoom} from 'swiper';
import {SwiperModule} from 'swiper/angular';
import {MobileBackCoordinatorService} from '../../../core/mobile/mobile-back-coordinator.service';
import {ImageLightboxItem, ImageLightboxThumbnail} from './image-lightbox.model';
import {reconcileOverlayScrollLock} from '../../../core/helpers/overlay-scroll-lock';

// Swiper 8 maintains this drag-click flag at runtime, but omits it from its typings.
interface LightboxSwiper extends Swiper {
  allowClick?: boolean;
}

Swiper.use([Zoom]);

@Component({
  selector: 'app-image-lightbox',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonModule, DialogModule, SkeletonModule, SwiperModule],
  templateUrl: './image-lightbox.component.html',
  styleUrl: './image-lightbox.component.scss',
})
export class ImageLightboxComponent implements OnChanges, AfterViewChecked, OnDestroy {
  private static nextSessionId = 0;
  @Input() images: ImageLightboxItem[] = [];
  @Input() imageId: string;
  @Input() thumbnail: ImageLightboxThumbnail = {};
  @Input() interactionMode: 'default' | 'chat' | 'gestures' = 'default';
  @Input() showTrigger = true;
  @Input() showThumbnails = false;
  @Output() loaded = new EventEmitter<string>();
  @Output() loadFailed = new EventEmitter<string>();
  @Output() retry = new EventEmitter<string>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() activeImageChange = new EventEmitter<string>();
  @Output() previewRequested = new EventEmitter<string>();
  @ViewChild(Dialog) dialog?: Dialog;
  @ViewChild('previewStage') previewStage?: ElementRef<HTMLElement>;

  @ViewChild('thumbnailRail') thumbnailRail?: ElementRef<HTMLElement>;

  readonly browser: boolean;
  readonly sessionId = `image-lightbox-${++ImageLightboxComponent.nextSessionId}`;
  readonly config: SwiperOptions = {
    slidesPerView: 1, speed: 300, loop: false, rewind: false,
    keyboard: false, watchOverflow: true, threshold: 5,
    preventClicks: true, preventClicksPropagation: true,
  };
  mounted = false;
  visible = false;
  activeIndex = 0;
  scale = 1;
  rotation = 0;
  private swiper?: LightboxSwiper;
  private sessionActive = false;
  private closing = false;
  private destroyed = false;
  private selectedId?: string;
  private trigger?: HTMLElement;
  private releaseBackHandler?: () => void;
  private previousScrollbarWidth = '';
  private readonly previewLoaded = new Set<string>();
  private readonly previewErrors = new Set<string>();
  private readonly naturalSizes = new Map<string, {width: number; height: number}>();
  private stageSize?: {width: number; height: number};
  private measuredStage?: HTMLElement;
  private stageResizeObserver?: ResizeObserver;
  private stageMeasureFrame?: number;
  private readonly animations = new Set<Animation>();
  private readonly ghosts = new Set<HTMLElement>();
  private transitionEpoch = 0;
  private openingFrame?: number;
  private transitionPicture?: HTMLElement;
  private stageCleanup?: () => void;
  private suppressClickUntil = 0;
  private lastRail?: HTMLElement;
  private lastRailImage?: string;
  private lastRailWidth = 0;
  private readonly thumbnailErrors = new Set<string>();
  private readonly previewReloading = new Set<string>();
  private drag?: {id: number; x: number; y: number; dy: number; axis?: 'x' | 'y'};

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly cdr: ChangeDetectorRef,
    private readonly mobileBack: MobileBackCoordinatorService,
    private readonly zone: NgZone,
  ) {
    this.browser = isPlatformBrowser(platformId);
  }

  get usesGestures(): boolean {
    return this.interactionMode === 'chat' || this.interactionMode === 'gestures';
  }

  get hasThumbnails(): boolean {
    return this.showThumbnails && this.images.length > 1;
  }

  ngAfterViewChecked(): void {
    this.observeStageSize();
    const rail = this.thumbnailRail?.nativeElement;
    if (!this.browser || !rail || (this.lastRail === rail && this.lastRailImage === this.selectedId && this.lastRailWidth === rail.clientWidth)) return;
    this.lastRail = rail;
    this.lastRailImage = this.selectedId;
    this.lastRailWidth = rail.clientWidth;
    const button = rail.querySelector<HTMLElement>('[aria-current="true"]');
    if (!button) return;
    // Scroll only this strip, rather than scrolling the page or enclosing dialog.
    if (button.offsetLeft < rail.scrollLeft) rail.scrollLeft = button.offsetLeft;
    else if (button.offsetLeft + button.offsetWidth > rail.scrollLeft + rail.clientWidth) {
      rail.scrollLeft = button.offsetLeft + button.offsetWidth - rail.clientWidth;
    }
  }

  private observeStageSize(): void {
    if (!this.browser || this.destroyed) return;
    const stage = this.usesGestures && this.mounted ? this.previewStage?.nativeElement : undefined;
    if (stage === this.measuredStage) return;
    this.stopStageMeasurement();
    this.measuredStage = stage;
    if (!stage) return;
    this.zone.runOutsideAngular(() => {
      if (typeof ResizeObserver !== 'undefined') {
        this.stageResizeObserver = new ResizeObserver(() => this.scheduleStageMeasurement());
        this.stageResizeObserver.observe(stage);
      }
      this.scheduleStageMeasurement();
    });
  }

  private scheduleStageMeasurement(): void {
    const stage = this.measuredStage;
    if (!stage || this.destroyed || this.stageMeasureFrame !== undefined) return;
    this.zone.runOutsideAngular(() => {
      this.stageMeasureFrame = requestAnimationFrame(() => {
        this.stageMeasureFrame = undefined;
        if (this.destroyed || this.measuredStage !== stage) return;
        const width = stage.clientWidth;
        const height = stage.clientHeight;
        if (this.stageSize?.width === width && this.stageSize?.height === height) return;
        this.zone.run(() => {
          this.stageSize = {width, height};
          this.cdr.markForCheck();
        });
      });
    });
  }

  private stopStageMeasurement(): void {
    this.stageResizeObserver?.disconnect();
    this.stageResizeObserver = undefined;
    if (this.stageMeasureFrame !== undefined) cancelAnimationFrame(this.stageMeasureFrame);
    this.stageMeasureFrame = undefined;
    this.measuredStage = undefined;
  }

  onThumbnailWheel(event: WheelEvent): void {
    const rail = this.thumbnailRail?.nativeElement;
    if (!rail || rail.scrollWidth <= rail.clientWidth || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rail.clientWidth : 1;
    rail.scrollLeft += event.deltaY * unit;
    event.preventDefault();
    event.stopPropagation();
  }

  isThumbnailError(item: ImageLightboxItem): boolean {
    return this.thumbnailErrors.has(`${item.id}|${item.thumbnailUrl}`);
  }

  onThumbnailError(item: ImageLightboxItem): void {
    this.thumbnailErrors.add(`${item.id}|${item.thumbnailUrl}`);
  }

  isPreviewReloading(item: ImageLightboxItem): boolean {
    return this.previewReloading.has(this.previewKey(item));
  }

  get image(): ImageLightboxItem | undefined {
    return this.images.find(item => item.id === this.imageId);
  }

  ngOnChanges(): void {
    this.config.speed = this.usesGestures && this.reducedMotion() ? 0 : 300;
    this.config.zoom = this.usesGestures ? {minRatio: 1, maxRatio: 3, toggle: false} : false;
    if (!this.sessionActive || this.closing) return;
    const index = this.images.findIndex(item => item.id === this.selectedId);
    if (index < 0 || (this.showTrigger && !this.image)) {
      this.close();
    } else if (index !== this.activeIndex) {
      this.activeIndex = index;
      this.swiper?.slideTo(index, 0);
    }
  }

  open(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.openImage(this.imageId, event.currentTarget as HTMLElement);
  }

  openImage(imageId: string, trigger?: HTMLElement): void {
    const item = this.images.find(image => image.id === imageId);
    if (!this.browser || this.destroyed || this.sessionActive || item?.state !== 'ready') return;
    this.activeIndex = this.images.indexOf(item);
    this.selectedId = imageId;
    this.trigger = trigger || this.document.activeElement as HTMLElement;
    if (this.usesGestures) {
      const image = this.trigger?.tagName === 'IMG' ? this.trigger as HTMLImageElement : this.trigger?.querySelector('img');
      if (image?.naturalWidth) this.naturalSizes.set(imageId, {width: image.naturalWidth, height: image.naturalHeight});
    }
    this.resetTransform();
    if (!this.usesGestures) this.previewLoaded.clear();
    this.previewErrors.clear();
    this.previousScrollbarWidth = this.document.body.style.getPropertyValue('--scrollbar-width');
    this.sessionActive = true;
    this.closing = false;
    this.mounted = true;
    this.visible = true;
    this.document.addEventListener('keydown', this.onKeydown, true);
    this.document.addEventListener('click', this.onOverlayClick, true);
    this.releaseBackHandler = this.mobileBack.register(this.sessionId, () => {
      this.zone.run(() => this.close());
      return true;
    });
    this.opened.emit();
    this.activeImageChange.emit(imageId);
    this.previewRequested.emit(imageId);
    this.cdr.markForCheck();
  }

  onShow(): void {
    this.dialog?.wrapper?.querySelector<HTMLButtonElement>('.image-lightbox-close')?.focus({preventScroll: true});
    if (this.usesGestures) {
      this.bindNativeGestures();
      this.cdr.detectChanges();
      this.openingFrame = requestAnimationFrame(() => {
        this.openingFrame = undefined;
        if (!this.closing && this.sessionActive) void this.animatePhoto(true);
      });
    }
  }

  onSwiper(swiper: Swiper): void {
    this.swiper = swiper;
  }

  onSlideChange(event: [Swiper]): void {
    // Swiper emits gesture callbacks outside Angular's zone.
    this.zone.run(() => {
      const previousId = this.selectedId;
      this.activeIndex = event[0].activeIndex;
      this.selectedId = this.images[this.activeIndex]?.id;
      if (this.selectedId) {
        this.previewRequested.emit(this.selectedId);
        if (previousId !== this.selectedId) this.activeImageChange.emit(this.selectedId);
      }
      this.resetTransform();
      this.cdr.markForCheck();
    });
  }

  navigate(step: number): void {
    if (!this.visible || this.closing || !this.swiper || this.swiper.destroyed) return;
    const index = Math.max(0, Math.min(this.images.length - 1, this.activeIndex + step));
    this.selectImage(index);
  }

  selectImage(index: number): void {
    if (!this.visible || this.closing || !this.swiper || this.swiper.destroyed || !this.images[index]) return;
    if (this.usesGestures) this.cancelTransition();
    this.resetTransform();
    this.swiper.slideTo(index, this.usesGestures && this.reducedMotion() ? 0 : 300);
  }

  zoom(step: number): void {
    if (this.closing) return;
    if (this.usesGestures) { this.cancelTransition(); this.setNativeZoom(this.scale + step); return; }
    this.scale = Math.max(0.5, Math.min(1.5, Math.round((this.scale + step) * 10) / 10));
    if (this.swiper) this.swiper.allowTouchMove = this.scale <= 1;
  }

  rotate(step: number): void {
    if (this.closing) return;
    if (this.usesGestures) { this.cancelTransition(); this.setNativeZoom(1); }
    this.rotation = (this.rotation + step) % 360;
  }

  private resetTransform(): void {
    if (this.usesGestures) this.setNativeZoom(1);
    this.scale = 1;
    this.rotation = 0;
    if (this.swiper && !this.swiper.destroyed) this.swiper.allowTouchMove = true;
  }

  trackImage(_: number, item: ImageLightboxItem): string {
    return item.id;
  }

  private previewKey(item: ImageLightboxItem): string {
    return `${item.id}|${item.previewUrl}`;
  }

  isPreviewReady(item: ImageLightboxItem): boolean {
    if (this.usesGestures && item.state === 'ready' && item.previewUrl && item.previewUrl === item.thumbnailUrl) return true;
    return !!item.previewUrl && item.state !== 'error' && this.previewLoaded.has(this.previewKey(item));
  }

  isPreviewError(item: ImageLightboxItem): boolean {
    return item.previewState === 'error' || item.state === 'error' || this.previewErrors.has(this.previewKey(item));
  }

  onPreviewLoaded(item: ImageLightboxItem, event?: Event): void {
    const image = event?.target as HTMLImageElement;
    if (image?.naturalWidth) this.naturalSizes.set(item.id, {width: image.naturalWidth, height: image.naturalHeight});
    this.previewLoaded.add(this.previewKey(item));
    this.previewErrors.delete(this.previewKey(item));
    if (item.previewState === undefined) this.loaded.emit(item.id);
  }

  onPreviewError(item: ImageLightboxItem): void {
    this.previewErrors.add(this.previewKey(item));
    if (item.previewState !== undefined) item.previewState = 'error';
    if (item.previewState === undefined) this.loadFailed.emit(item.id);
  }

  retryImage(item: ImageLightboxItem): void {
    this.previewErrors.delete(this.previewKey(item));
    this.previewLoaded.delete(this.previewKey(item));
    if (this.visible && item.previewState !== undefined) this.previewRequested.emit(item.id);
    else {
      this.retry.emit(item.id);
      if (this.visible && this.browser) {
        // Public images have no parent loader to replace their URL. Remount the
        // failed image so retry also works when its URL has not changed.
        const key = this.previewKey(item);
        this.previewReloading.add(key);
        this.cdr.detectChanges();
        queueMicrotask(() => {
          this.previewReloading.delete(key);
          if (!this.destroyed) this.cdr.markForCheck();
        });
      }
    }
  }

  close(): void {
    if (!this.sessionActive || this.closing) return;
    this.closing = true;
    if (this.usesGestures && this.dialog?.container) {
      this.cancelTransition();
      const epoch = this.transitionEpoch;
      void this.animatePhoto(false).then(() => {
        if (!this.destroyed && this.sessionActive && epoch === this.transitionEpoch) this.zone.run(() => this.hideDialog());
      });
      return;
    }
    this.hideDialog();
  }

  private hideDialog(): void {
    this.visible = false;
    this.restoreBodyScrollLock();
    this.cdr.markForCheck();
    // Back can arrive before the dialog's first render; no animation/onHide
    // will run in that case, so complete the session here.
    if (!this.dialog?.container) this.onHide();
  }

  onHide(): void {
    this.finishSession(true);
  }

  private finishSession(restoreFocus: boolean): void {
    if (!this.sessionActive) return;
    this.sessionActive = false;
    this.stopStageMeasurement();
    this.stageSize = undefined;
    this.stageCleanup?.(); this.stageCleanup = undefined;
    this.cancelTransition();
    this.drag = undefined;
    this.visible = false;
    this.mounted = false;
    if (this.swiper && !this.swiper.destroyed) this.swiper.destroy(true, true);
    this.swiper = undefined;
    this.document.removeEventListener('keydown', this.onKeydown, true);
    this.document.removeEventListener('click', this.onOverlayClick, true);
    this.releaseBackHandler?.();
    this.releaseBackHandler = undefined;
    this.restoreBodyScrollLock();
    this.lastRail = undefined;
    this.lastRailImage = undefined;
    if (restoreFocus && !this.destroyed && this.trigger?.isConnected) this.trigger.focus({preventScroll: true});
    this.trigger = undefined;
    if (!this.destroyed) {
      this.cdr.markForCheck();
      this.closed.emit();
    }
  }

  private readonly onKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' && !event.key.startsWith('Arrow')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.zone.run(() => {
      if (event.key === 'Escape') this.close();
      else if (event.key === 'ArrowLeft') this.navigate(-1);
      else if (event.key === 'ArrowRight') this.navigate(1);
    });
  };

  private readonly onOverlayClick = (event: MouseEvent): void => {
    const target = event.target as Element | null;
    const wrapper = this.dialog?.wrapper;
    if (!target || !wrapper?.contains(target)) return;
    const close = target.closest('.image-lightbox-close');
    if (!close && this.usesGestures && (this.drag?.axis === 'y' || Date.now() < this.suppressClickUntil)) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    // Swiper deliberately suppresses the click following a recognized drag.
    if (!close && this.swiper?.allowClick === false) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const background = !target.closest('.image-lightbox-picture, .image-lightbox-zoom-target, button, .image-lightbox-toolbar, .image-lightbox-thumbnails');
    if (close || background) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.zone.run(() => this.close());
    }
  };

  onZoomChange(event: [Swiper, number, ...unknown[]]): void {
    if (!this.usesGestures) return;
    this.zone.run(() => {
      this.scale = Math.max(1, Math.min(3, event[1]));
      if (this.swiper) this.swiper.allowTouchMove = this.scale <= 1.01;
      this.cdr.markForCheck();
    });
  }

  onDoubleTap(event: [Swiper, Event]): void {
    if (!this.closing && this.usesGestures && Date.now() >= this.suppressClickUntil) this.zone.run(() => this.setNativeZoom(this.scale > 1.01 ? 1 : 2, event[1]));
  }

  private setNativeZoom(value: number, event?: Event): void {
    if (!this.swiper?.zoom?.enabled) return;
    value = Math.max(1, Math.min(3, Math.round(value * 10) / 10));
    if (value <= 1) this.swiper.zoom.out();
    else {
      const container = this.previewStage?.nativeElement.querySelector('.swiper-slide-active .swiper-zoom-container');
      container?.setAttribute('data-swiper-zoom', String(value));
      (this.swiper.zoom.in as (event?: Event) => void)(event);
      container?.removeAttribute('data-swiper-zoom');
    }
    if (this.reducedMotion()) this.previewStage?.nativeElement.querySelectorAll<HTMLElement>('.swiper-zoom-container, .swiper-zoom-target')
      .forEach(element => element.style.transitionDuration = '0ms');
    this.scale = value;
    this.swiper.allowTouchMove = value <= 1;
    this.cdr.markForCheck();
  }

  chatImageBox(item: ImageLightboxItem, index: number): {width: number; height: number; imageWidth: number; imageHeight: number} | null {
    const size = this.naturalSizes.get(item.id) || (item.width && item.height ? {width: item.width, height: item.height} : null);
    // Template bindings must not depend on layout changes within the current render.
    const stage = this.stageSize;
    if (!size || !stage?.width || !stage.height) return null;
    const rotated = index === this.activeIndex && Math.abs(this.rotation % 180) === 90;
    const width = rotated ? size.height : size.width;
    const height = rotated ? size.width : size.height;
    const ratio = Math.min(stage.width / width, stage.height / height);
    return {width: width * ratio, height: height * ratio, imageWidth: size.width * ratio, imageHeight: size.height * ratio};
  }

  private bindNativeGestures(): void {
    this.stageCleanup?.();
    const stage = this.previewStage?.nativeElement;
    if (!stage) return;
    const start = (event: TouchEvent) => {
      if (this.closing) { event.stopImmediatePropagation(); return; }
      this.cancelTransition();
      if (this.drag?.axis === 'y') this.resetDrag(stage);
      this.drag = undefined;
      if (this.scale > 1.01 || event.touches.length !== 1 || (event.target as Element)?.closest('button')) return;
      const touch = event.touches[0];
      const viewportWidth = this.document.defaultView?.innerWidth || stage.clientWidth;
      if (touch.clientX <= 24 || touch.clientX >= viewportWidth - 24) return;
      this.drag = {id: touch.identifier, x: touch.clientX, y: touch.clientY, dy: 0};
    };
    const move = (event: TouchEvent) => {
      const drag = this.drag;
      if (!drag) return;
      if (event.touches.length !== 1 || this.scale > 1.01) { this.resetDrag(stage); return; }
      const touch = Array.from(event.touches).find(item => item.identifier === drag.id);
      if (!touch) { this.resetDrag(stage); return; }
      const dx = touch.clientX - drag.x;
      const dy = touch.clientY - drag.y;
      if (!drag.axis && Math.hypot(dx, dy) >= 10) drag.axis = Math.abs(dy) > Math.abs(dx) * 1.2 ? 'y' : 'x';
      if (drag.axis !== 'y') return;
      event.preventDefault(); event.stopImmediatePropagation();
      drag.dy = dy;
      if (this.swiper) { this.swiper.allowTouchMove = false; this.swiper.allowClick = false; }
      this.suppressClickUntil = Date.now() + 350;
      stage.style.transform = `translate3d(0,${dy}px,0)`;
    };
    const end = (event: TouchEvent) => {
      const drag = this.drag;
      if (!drag) return;
      const vertical = drag.axis === 'y';
      if (vertical) { event.preventDefault(); this.suppressClickUntil = Date.now() + 350; }
      const dismiss = vertical && event.type !== 'touchcancel' && Math.abs(drag.dy) >= Math.min(120, stage.clientHeight * .2);
      this.drag = undefined;
      if (this.swiper) this.swiper.allowTouchMove = this.scale <= 1.01;
      if (dismiss) this.zone.run(() => this.close());
      else this.resetDrag(stage);
    };
    const resize = () => this.zone.run(() => { this.resetDrag(stage); this.setNativeZoom(1); this.cdr.markForCheck(); });
    stage.addEventListener('touchstart', start, {capture: true, passive: true});
    stage.addEventListener('touchmove', move, {capture: true, passive: false});
    stage.addEventListener('touchend', end, {capture: true, passive: false});
    stage.addEventListener('touchcancel', end, {capture: true, passive: false});
    this.document.defaultView?.addEventListener('resize', resize);
    this.stageCleanup = () => {
      stage.removeEventListener('touchstart', start, true); stage.removeEventListener('touchmove', move, true);
      stage.removeEventListener('touchend', end, true); stage.removeEventListener('touchcancel', end, true);
      this.document.defaultView?.removeEventListener('resize', resize);
      stage.style.transform = '';
    };
  }

  private resetDrag(stage: HTMLElement): void {
    this.drag = undefined;
    if (stage.style.transform && stage.animate && !this.reducedMotion()) {
      const animation = stage.animate([{transform: stage.style.transform}, {transform: 'none'}], {duration: 220, easing: 'ease-out'});
      this.trackAnimation(animation);
    }
    stage.style.transform = '';
    if (this.swiper) this.swiper.allowTouchMove = this.scale <= 1.01;
  }

  private reducedMotion(): boolean {
    return !!this.document.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  private trackAnimation(animation: Animation): Promise<void> {
    this.animations.add(animation);
    return animation.finished.catch(() => undefined).then(() => { this.animations.delete(animation); });
  }

  private async animatePhoto(opening: boolean): Promise<void> {
    const epoch = this.transitionEpoch;
    const wrapper = this.dialog?.wrapper;
    if (!wrapper || !wrapper.animate || this.reducedMotion()) return;
    const selected = this.images[this.activeIndex];
    const trigger = Array.from(this.document.querySelectorAll<HTMLButtonElement>('[data-lightbox-image]'))
      .find(element => element.dataset['lightboxImage'] === selected?.id && element.getBoundingClientRect().width > 0);
    const tile = trigger?.querySelector('img')?.getBoundingClientRect();
    const picture = wrapper.querySelector<HTMLElement>('.swiper-slide-active .image-lightbox-zoom-target');
    const target = picture?.getBoundingClientRect();
    const frames: Promise<void>[] = [this.trackAnimation(wrapper.animate([{opacity: opening ? 0 : 1}, {opacity: opening ? 1 : 0}], {duration: 220, easing: 'ease-out', fill: opening ? 'none' : 'forwards'}))];
    if (tile?.width && tile.height && target?.width && target.height && selected?.previewUrl && this.rotation === 0) {
      const ghost = this.document.createElement('img');
      ghost.src = selected.previewUrl;
      ghost.alt = '';
      ghost.setAttribute('aria-hidden', 'true');
      Object.assign(ghost.style, {position: 'fixed', zIndex: String(Math.max(6005, Number(this.document.defaultView?.getComputedStyle(wrapper).zIndex) || 0) + 1), pointerEvents: 'none', objectFit: 'contain',
        left: `${target.left}px`, top: `${target.top}px`, width: `${target.width}px`, height: `${target.height}px`, transformOrigin: 'center'});
      this.document.body.appendChild(ghost); this.ghosts.add(ghost);
      this.transitionPicture = picture; picture.style.opacity = '0';
      const scale = Math.max(tile.width / target.width, tile.height / target.height);
      const x = tile.left + tile.width / 2 - target.left - target.width / 2;
      const y = tile.top + tile.height / 2 - target.top - target.height / 2;
      const horizontal = Math.max(0, (target.width - tile.width / scale) / 2);
      const vertical = Math.max(0, (target.height - tile.height / scale) / 2);
      const small = {transform: `translate(${x}px,${y}px) scale(${scale})`, clipPath: `inset(${vertical}px ${horizontal}px round 10px)`};
      const large = {transform: 'translate(0px,0px) scale(1)', clipPath: 'inset(0px 0px round 0px)'};
      frames.push(this.trackAnimation(ghost.animate(opening ? [small, large] : [large, small], {duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'both'})));
      await Promise.all(frames);
      ghost.remove(); this.ghosts.delete(ghost);
      if (epoch === this.transitionEpoch && picture === this.transitionPicture) { picture.style.opacity = ''; this.transitionPicture = undefined; }
    } else await Promise.all(frames);
  }

  private cancelTransition(): void {
    this.transitionEpoch++;
    if (this.openingFrame !== undefined) cancelAnimationFrame(this.openingFrame);
    this.openingFrame = undefined;
    this.animations.forEach(animation => animation.cancel()); this.animations.clear();
    this.ghosts.forEach(ghost => ghost.remove()); this.ghosts.clear();
    if (this.transitionPicture) this.transitionPicture.style.opacity = '';
    this.transitionPicture = undefined;
  }

  private restoreBodyScrollLock(): void {
    reconcileOverlayScrollLock(this.document, this.dialog?.wrapper, this.previousScrollbarWidth);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopStageMeasurement();
    if (!this.browser || !this.sessionActive) return;
    const wrapper = this.dialog?.wrapper;
    // PrimeNG appends the dialog outside this component. Release it before
    // Angular removes the parent, preserving an enclosing modal's scroll lock.
    this.dialog?.onContainerDestroy();
    wrapper?.remove();
    this.finishSession(false);
  }
}
